import React, { useState, useEffect } from "react";
import RevenueService, {
  RevenueKPIs,
  DailyAnalytics,
  ProviderPerformance,
  ServicePopularity,
  RevenueTransaction,
  DoctorEarnings,
} from "../services/revenueService";

// TypeScript interfaces for component props
interface SimpleLineChartProps {
  data: DailyAnalytics[];
  height?: number;
  color?: string;
}

interface OwnerEarnings {
  owner_id: number;
  owner_name: string;
  owner_role: string;
  total_earned: number;
  transactions_count: number;
  avg_per_transaction: number;
  equipment_used: {
    equipment_id: number;
    equipment_name: string;
    equipment_type: string;
    usage_count: number;
    earned: number;
    avg_per_use: number;
  }[];
  services_used: {
    service_name: string;
    count: number;
    earned: number;
  }[];
  recent_usages: {
    date: string;
    patient_name: string;
    service: string;
    equipment: string;
    earned: number;
  }[];
}

interface OwnerEarningsCardProps {
  owner: OwnerEarnings;
  colorTheme: ColorTheme;
}

interface ReferrerEarnings {
  referrer_id: number | null;
  referrer_name: string;
  total_earned: number;
  transactions_count: number;
  avg_per_referral: number;
  services_referred: {
    service_name: string;
    count: number;
    earned: number;
  }[];
  recent_referrals: {
    date: string;
    patient_name: string;
    service: string;
    earned: number;
  }[];
}

interface ReferrerEarningsCardProps {
  referrer: ReferrerEarnings;
  colorTheme: ColorTheme;
}
interface SimpleBarChartProps {
  data: any[];
  labelKey: string;
  valueKey: string;
  height?: number;
  color?: string;
}

interface DoctorEarningsCardProps {
  doctor: DoctorEarnings;
  colorTheme: ColorTheme;
}

interface TransactionCardProps {
  transaction: RevenueTransaction;
  colorTheme: ColorTheme;
}

interface ColorTheme {
  primary: string;
  success: string;
  warning: string;
  error: string;
  charity: string;
  gray50: string;
  gray100: string;
  gray200: string;
  gray500: string;
  gray600: string;
  gray700: string;
  gray900: string;
  cardBg: string;
}

// Simple Chart Components
const SimpleLineChart: React.FC<SimpleLineChartProps> = ({
  data,
  height = 200,
  color = "#3b82f6",
}) => {
  if (data.length === 0) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#6b7280",
          fontSize: "14px",
        }}
      >
        No data available
      </div>
    );
  }

  const maxValue = Math.max(
    ...data.map((d: DailyAnalytics) => Number(d.total_revenue) || 0)
  );
  const minValue = Math.min(
    ...data.map((d: DailyAnalytics) => Number(d.total_revenue) || 0)
  );
  const range = maxValue - minValue || 1;

  const formatDate = (dateString: string): string => {
    if (dateString.includes("T")) {
      return dateString.split("T")[0];
    }
    return dateString;
  };

  const formatDisplayDate = (dateString: string): string => {
    const date = formatDate(dateString);
    const [year, month, day] = date.split("-");
    return `${month}-${day}`;
  };

  return (
    <div style={{ height, position: "relative", padding: "20px 0" }}>
      <svg width="100%" height="100%" style={{ overflow: "visible" }}>
        {data.map((point: DailyAnalytics, index: number) => {
          const x = data.length > 1 ? (index / (data.length - 1)) * 100 : 50;
          const pointRevenue = Number(point.total_revenue) || 0;
          const y = 100 - ((pointRevenue - minValue) / range) * 80;
          const nextPoint = data[index + 1];

          return (
            <g key={index}>
              {nextPoint && (
                <line
                  x1={`${x}%`}
                  y1={`${y}%`}
                  x2={`${
                    data.length > 1
                      ? ((index + 1) / (data.length - 1)) * 100
                      : 50
                  }%`}
                  y2={`${
                    100 -
                    (((Number(nextPoint.total_revenue) || 0) - minValue) /
                      range) *
                      80
                  }%`}
                  stroke={color}
                  strokeWidth="2"
                />
              )}
              <circle
                cx={`${x}%`}
                cy={`${y}%`}
                r="4"
                fill={color}
                style={{ cursor: "pointer" }}
              >
                <title>{`${formatDate(
                  point.transaction_date
                )}: $${pointRevenue.toFixed(2)}`}</title>
              </circle>
            </g>
          );
        })}
      </svg>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "10px",
          fontSize: "12px",
          color: "#6b7280",
        }}
      >
        <span>{formatDisplayDate(data[0]?.transaction_date)}</span>
        <span>
          {formatDisplayDate(
            data[Math.floor(data.length / 2)]?.transaction_date
          )}
        </span>
        <span>
          {formatDisplayDate(data[data.length - 1]?.transaction_date)}
        </span>
      </div>
    </div>
  );
};
// Referrer Earnings Card Component
const ReferrerEarningsCard: React.FC<ReferrerEarningsCardProps> = ({
  referrer,
  colorTheme,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <div
      style={{
        background: colorTheme.cardBg,
        borderRadius: "16px",
        padding: "24px",
        border: "1px solid #f1f5f9",
        marginBottom: "16px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
      }}
    >
      {/* Referrer Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "16px",
        }}
      >
        <div>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: colorTheme.gray900,
              margin: "0 0 4px 0",
            }}
          >
            🤝 {referrer.referrer_name}
          </h3>
          <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
            <span style={{ color: colorTheme.gray500 }}>
              {referrer.transactions_count} referrals
            </span>
            {/* <span style={{ color: colorTheme.gray500 }}>
              ID: {referrer.referrer_id || "N/A"}
            </span> */}
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#8b5cf6", // Purple color for referrers
            }}
          >
            {RevenueService.formatCurrency(referrer.total_earned)}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: colorTheme.gray500,
              marginTop: "4px",
            }}
          >
            Total earnings
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "16px",
          marginBottom: "16px",
          padding: "16px",
          background: colorTheme.gray50,
          borderRadius: "12px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#8b5cf6",
            }}
          >
            {RevenueService.formatCurrency(referrer.avg_per_referral)}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Avg per Referral
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.success,
            }}
          >
            {referrer.transactions_count}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Total Referrals
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.warning,
            }}
          >
            {referrer.services_referred.length}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Service Types
          </div>
        </div>
      </div>

      {/* Services Breakdown Toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: "100%",
          padding: "12px",
          background: expanded ? "#8b5cf6" : colorTheme.gray100,
          color: expanded ? "white" : colorTheme.gray700,
          border: "none",
          borderRadius: "8px",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "500",
          transition: "all 0.3s ease",
        }}
      >
        {expanded ? "Hide" : "Show"} Referral Breakdown {expanded ? "▲" : "▼"}
      </button>

      {/* Expanded Services Details */}
      {expanded && (
        <div
          style={{
            marginTop: "16px",
            padding: "16px",
            background: colorTheme.gray50,
            borderRadius: "8px",
          }}
        >
          <h4
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: colorTheme.gray900,
              marginBottom: "12px",
            }}
          >
            Services Referred
          </h4>
          {referrer.services_referred.map((service: any, index: number) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 0",
                borderBottom:
                  index < referrer.services_referred.length - 1
                    ? `1px solid ${colorTheme.gray200}`
                    : "none",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "500",
                    color: colorTheme.gray900,
                  }}
                >
                  {service.service_name}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: colorTheme.gray600,
                  }}
                >
                  {service.count} referrals •{" "}
                  {RevenueService.formatCurrency(
                    service.earned / service.count
                  )}{" "}
                  per referral
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#8b5cf6",
                  }}
                >
                  {RevenueService.formatCurrency(service.earned)}
                </div>
              </div>
            </div>
          ))}

          {/* Recent Referrals */}
          {referrer.recent_referrals.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: colorTheme.gray900,
                  marginBottom: "12px",
                }}
              >
                Recent Referrals
              </h4>
              {referrer.recent_referrals
                .slice(0, 3)
                .map((referral: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 0",
                      fontSize: "12px",
                    }}
                  >
                    <div>
                      <span
                        style={{ color: colorTheme.gray900, fontWeight: "500" }}
                      >
                        {referral.patient_name}
                      </span>
                      <span
                        style={{ color: colorTheme.gray500, margin: "0 8px" }}
                      >
                        •
                      </span>
                      <span style={{ color: colorTheme.gray600 }}>
                        {referral.service}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: "#8b5cf6", fontWeight: "600" }}>
                        {RevenueService.formatCurrency(referral.earned)}
                      </span>
                      <span
                        style={{ color: colorTheme.gray500, marginLeft: "8px" }}
                      >
                        {new Date(referral.date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: "#8b5cf6",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          💰 Process Payment
        </button>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.primary,
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          📊 View Details
        </button>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.gray200,
            color: colorTheme.gray700,
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          📧 Send Report
        </button>
      </div>
    </div>
  );
};

// Sponser Earnings Card Component
const OwnerEarningsCard: React.FC<OwnerEarningsCardProps> = ({
  owner,
  colorTheme,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <div
      style={{
        background: colorTheme.cardBg,
        borderRadius: "16px",
        padding: "24px",
        border: "1px solid #f1f5f9",
        marginBottom: "16px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
      }}
    >
      {/* Sponser Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "16px",
        }}
      >
        <div>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: colorTheme.gray900,
              margin: "0 0 4px 0",
            }}
          >
            👑 {owner.owner_name}
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: colorTheme.gray600,
              margin: "0 0 8px 0",
            }}
          >
            {owner.owner_role}
          </p>
          <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
            <span style={{ color: colorTheme.gray500 }}>
              {owner.transactions_count} equipment uses
            </span>
            <span style={{ color: colorTheme.gray500 }}>
              {owner.equipment_used.length} equipment types
            </span>
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: colorTheme.warning, // Orange/Gold color for owners
            }}
          >
            {RevenueService.formatCurrency(owner.total_earned)}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: colorTheme.gray500,
              marginTop: "4px",
            }}
          >
            Equipment earnings
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "16px",
          marginBottom: "16px",
          padding: "16px",
          background: colorTheme.gray50,
          borderRadius: "12px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.warning,
            }}
          >
            {RevenueService.formatCurrency(owner.avg_per_transaction)}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Avg per Use
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.success,
            }}
          >
            {owner.transactions_count}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Total Uses
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.primary,
            }}
          >
            {owner.equipment_used.length}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Equipment Types
          </div>
        </div>
      </div>

      {/* Equipment Breakdown Toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: "100%",
          padding: "12px",
          background: expanded ? colorTheme.warning : colorTheme.gray100,
          color: expanded ? "white" : colorTheme.gray700,
          border: "none",
          borderRadius: "8px",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "500",
          transition: "all 0.3s ease",
        }}
      >
        {expanded ? "Hide" : "Show"} Equipment Breakdown {expanded ? "▲" : "▼"}
      </button>

      {/* Expanded Equipment Details */}
      {expanded && (
        <div
          style={{
            marginTop: "16px",
            padding: "16px",
            background: colorTheme.gray50,
            borderRadius: "8px",
          }}
        >
          <h4
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: colorTheme.gray900,
              marginBottom: "12px",
            }}
          >
            Equipment Usage
          </h4>
          {owner.equipment_used.map((equipment: any, index: number) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 0",
                borderBottom:
                  index < owner.equipment_used.length - 1
                    ? `1px solid ${colorTheme.gray200}`
                    : "none",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "500",
                    color: colorTheme.gray900,
                  }}
                >
                  {equipment.equipment_name}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: colorTheme.gray600,
                  }}
                >
                  {equipment.equipment_type} • {equipment.usage_count} uses •{" "}
                  {RevenueService.formatCurrency(equipment.avg_per_use)} per use
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colorTheme.warning,
                  }}
                >
                  {RevenueService.formatCurrency(equipment.earned)}
                </div>
              </div>
            </div>
          ))}

          {/* Services Using Owner's Equipment */}
          {owner.services_used.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: colorTheme.gray900,
                  marginBottom: "12px",
                }}
              >
                Services Using Equipment
              </h4>
              {owner.services_used
                .slice(0, 5)
                .map((service: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 0",
                      fontSize: "12px",
                    }}
                  >
                    <div>
                      <span
                        style={{ color: colorTheme.gray900, fontWeight: "500" }}
                      >
                        {service.service_name}
                      </span>
                      <span
                        style={{ color: colorTheme.gray500, margin: "0 8px" }}
                      >
                        •
                      </span>
                      <span style={{ color: colorTheme.gray600 }}>
                        {service.count} times
                      </span>
                    </div>
                    <div>
                      <span
                        style={{ color: colorTheme.warning, fontWeight: "600" }}
                      >
                        {RevenueService.formatCurrency(service.earned)}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* Recent Equipment Usage */}
          {owner.recent_usages.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: colorTheme.gray900,
                  marginBottom: "12px",
                }}
              >
                Recent Equipment Usage
              </h4>
              {owner.recent_usages
                .slice(0, 3)
                .map((usage: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 0",
                      fontSize: "12px",
                    }}
                  >
                    <div>
                      <span
                        style={{ color: colorTheme.gray900, fontWeight: "500" }}
                      >
                        {usage.patient_name}
                      </span>
                      <span
                        style={{ color: colorTheme.gray500, margin: "0 4px" }}
                      >
                        •
                      </span>
                      <span style={{ color: colorTheme.gray600 }}>
                        {usage.equipment}
                      </span>
                    </div>
                    <div>
                      <span
                        style={{ color: colorTheme.warning, fontWeight: "600" }}
                      >
                        {RevenueService.formatCurrency(usage.earned)}
                      </span>
                      <span
                        style={{ color: colorTheme.gray500, marginLeft: "8px" }}
                      >
                        {new Date(usage.date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.warning,
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          💰 Process Payment
        </button>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.primary,
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          🏭 Equipment Report
        </button>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.gray200,
            color: colorTheme.gray700,
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          📧 Send Report
        </button>
      </div>
    </div>
  );
};

const SimpleBarChart: React.FC<SimpleBarChartProps> = ({
  data,
  labelKey,
  valueKey,
  height = 300,
  color = "#10b981",
}) => {
  if (data.length === 0) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#6b7280",
          fontSize: "14px",
        }}
      >
        No data available
      </div>
    );
  }

  const maxValue = Math.max(...data.map((d: any) => Number(d[valueKey]) || 0));

  return (
    <div
      style={{
        height,
        padding: "20px 0",
        display: "flex",
        alignItems: "end",
        gap: "12px",
      }}
    >
      {data.slice(0, 10).map((item: any, index: number) => {
        const itemValue = Number(item[valueKey]) || 0;
        const barHeight = (itemValue / maxValue) * (height - 80);

        return (
          <div
            key={index}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: 1,
              minWidth: "60px",
            }}
          >
            <div
              style={{
                height: `${Math.max(barHeight, 20)}px`,
                backgroundColor: color,
                width: "100%",
                borderRadius: "2px 2px 0 0",
                transition: "all 0.3s ease",
                position: "relative",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                paddingTop: "4px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "white",
                  fontWeight: "600",
                  textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                }}
              >
                {itemValue}
              </div>
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "#4b5563",
                textAlign: "center",
                marginTop: "8px",
                wordBreak: "break-word",
                lineHeight: "1.2",
                maxWidth: "100%",
              }}
            >
              {item[labelKey]}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Doctor Earnings Card Component
const DoctorEarningsCard: React.FC<DoctorEarningsCardProps> = ({
  doctor,
  colorTheme,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <div
      style={{
        background: colorTheme.cardBg,
        borderRadius: "16px",
        padding: "24px",
        border: "1px solid #f1f5f9",
        marginBottom: "16px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
      }}
    >
      {/* Doctor Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "16px",
        }}
      >
        <div>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: colorTheme.gray900,
              margin: "0 0 4px 0",
            }}
          >
            👨‍⚕️ {doctor.doctor_name}
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: colorTheme.gray600,
              margin: "0 0 8px 0",
            }}
          >
            {doctor.specialization}
          </p>
          <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
            <span style={{ color: colorTheme.gray500 }}>
              {doctor.transactions_count} transactions
            </span>
            <span style={{ color: colorTheme.gray500 }}>
              Last payment:{" "}
              {doctor.last_payment_date
                ? new Date(doctor.last_payment_date).toLocaleDateString()
                : "No payments yet"}
            </span>
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: colorTheme.gray900,
            }}
          >
            {RevenueService.formatCurrency(doctor.total_earned)}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: RevenueService.getGrowthColor(doctor.growth_percentage),
              fontWeight: "500",
            }}
          >
            {RevenueService.formatPercentage(doctor.growth_percentage)} vs last
            period
          </div>
          {doctor.pending_amount > 0 && (
            <div
              style={{
                fontSize: "12px",
                color: colorTheme.warning,
                fontWeight: "600",
                marginTop: "4px",
              }}
            >
              Pending: {RevenueService.formatCurrency(doctor.pending_amount)}
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "16px",
          marginBottom: "16px",
          padding: "16px",
          background: colorTheme.gray50,
          borderRadius: "12px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.primary,
            }}
          >
            {RevenueService.formatCurrency(doctor.avg_per_transaction)}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Avg per Transaction
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.success,
            }}
          >
            {doctor.transactions_count}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Total Services
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.warning,
            }}
          >
            {doctor.services_provided.length}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Service Types
          </div>
        </div>
      </div>

      {/* Services Breakdown Toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: "100%",
          padding: "12px",
          background: expanded ? colorTheme.primary : colorTheme.gray100,
          color: expanded ? "white" : colorTheme.gray700,
          border: "none",
          borderRadius: "8px",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "500",
          transition: "all 0.3s ease",
        }}
      >
        {expanded ? "Hide" : "Show"} Service Breakdown {expanded ? "▲" : "▼"}
      </button>

      {/* Expanded Services Details */}
      {expanded && (
        <div
          style={{
            marginTop: "16px",
            padding: "16px",
            background: colorTheme.gray50,
            borderRadius: "8px",
          }}
        >
          <h4
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: colorTheme.gray900,
              marginBottom: "12px",
            }}
          >
            Services Provided
          </h4>
          {doctor.services_provided.map((service: any, index: number) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 0",
                borderBottom:
                  index < doctor.services_provided.length - 1
                    ? `1px solid ${colorTheme.gray200}`
                    : "none",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "500",
                    color: colorTheme.gray900,
                  }}
                >
                  {service.name}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: colorTheme.gray600,
                  }}
                >
                  {service.count} times •{" "}
                  {RevenueService.formatCurrency(
                    service.earned / service.count
                  )}{" "}
                  per service
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colorTheme.success,
                  }}
                >
                  {RevenueService.formatCurrency(service.earned)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.success,
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          💰 Process Payment
        </button>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.primary,
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          📊 View Details
        </button>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.gray200,
            color: colorTheme.gray700,
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          📧 Send Report
        </button>
      </div>
    </div>
  );
};
const DoctorTransactionCard: React.FC<TransactionCardProps> = ({
  transaction,
  colorTheme,
}) => {
  return (
    <div
      style={{
        background: colorTheme.cardBg,
        borderRadius: "12px",
        padding: "16px",
        border: "1px solid #f1f5f9",
        marginBottom: "12px",
      }}
    >
      {/* Transaction Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "12px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: colorTheme.gray900,
            }}
          >
            {transaction.appointment?.patient?.first_name}{" "}
            {transaction.appointment?.patient?.last_name}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: colorTheme.gray500,
            }}
          >
            {new Date(transaction.transaction_date).toLocaleDateString()}
          </div>
        </div>
        <div
          style={{
            fontSize: "16px",
            fontWeight: "700",
            color: colorTheme.success,
          }}
        >
          {RevenueService.formatCurrency(transaction.provider_amount)}
        </div>
      </div>

      {/* Service Info */}
      <div
        style={{
          background: colorTheme.gray50,
          borderRadius: "8px",
          padding: "8px 12px",
          marginBottom: "12px",
          fontSize: "12px",
        }}
      >
        <div style={{ marginBottom: "4px" }}>
          <strong>Service:</strong>{" "}
          {transaction.service?.name || "Unknown Service"}
        </div>
        <div>
          <strong>My Earnings:</strong>{" "}
          <span style={{ color: colorTheme.success, fontWeight: "600" }}>
            {RevenueService.formatCurrency(transaction.provider_amount)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Enhanced Transaction Card Component
const TransactionCard: React.FC<TransactionCardProps> = ({
  transaction,
  colorTheme,
}) => {
  return (
    <div
      style={{
        background: colorTheme.cardBg,
        borderRadius: "12px",
        padding: "16px",
        border: "1px solid #f1f5f9",
        marginBottom: "12px",
      }}
    >
      {/* Transaction Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "12px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: colorTheme.gray900,
            }}
          >
            {transaction.appointment?.patient?.first_name}{" "}
            {transaction.appointment?.patient?.last_name}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: colorTheme.gray500,
            }}
          >
            {new Date(transaction.transaction_date).toLocaleDateString()}
          </div>
        </div>
        <div
          style={{
            fontSize: "16px",
            fontWeight: "700",
            color: colorTheme.gray900,
          }}
        >
          {RevenueService.formatCurrency(transaction.total_amount)}
        </div>
      </div>

      {/* Service & Provider Info */}
      <div
        style={{
          background: colorTheme.gray50,
          borderRadius: "8px",
          padding: "8px 12px",
          marginBottom: "12px",
          fontSize: "12px",
        }}
      >
        <div style={{ marginBottom: "4px" }}>
          <strong>Service:</strong>{" "}
          {transaction.service?.name || "Unknown Service"}
        </div>
        <div>
          <strong>Worker:</strong> {transaction.service?.provider?.first_name}{" "}
          {transaction.service?.provider?.last_name}
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "8px",
          fontSize: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ color: colorTheme.gray600 }}>🏥 Clinic:</span>
          <span style={{ fontWeight: "500", color: colorTheme.primary }}>
            {RevenueService.formatCurrency(transaction.clinic_amount)}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ color: colorTheme.gray600 }}>👨‍⚕️ Worker:</span>
          <span style={{ fontWeight: "500", color: colorTheme.success }}>
            {RevenueService.formatCurrency(transaction.provider_amount)}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ color: colorTheme.gray600 }}>👑 Owner:</span>
          <span style={{ fontWeight: "500", color: colorTheme.warning }}>
            {RevenueService.formatCurrency(transaction.owner_amount || 0)}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ color: colorTheme.gray600 }}>❤️ Charity:</span>
          <span style={{ fontWeight: "500", color: colorTheme.charity }}>
            {RevenueService.formatCurrency(transaction.charity_amount || 0)}
          </span>
        </div>
        {(transaction.referrer_amount > 0 || transaction.referrer_name) && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gridColumn: "1 / -1",
            }}
          >
            <span style={{ color: colorTheme.gray600 }}>
              🤝 Referrer ({transaction.referrer_name || "Unknown"}):
            </span>
            <span style={{ fontWeight: "500", color: "#8b5cf6" }}>
              {RevenueService.formatCurrency(transaction.referrer_amount || 0)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const RevenueDashboard: React.FC = () => {
  const [kpis, setKpis] = useState<RevenueKPIs | null>(null);
  const [dailyData, setDailyData] = useState<DailyAnalytics[]>([]);
  const [referrerEarnings, setReferrerEarnings] = useState<ReferrerEarnings[]>(
    []
  );
  const [ownerEarnings, setOwnerEarnings] = useState<OwnerEarnings[]>([]);
  const [providerPerformance, setProviderPerformance] = useState<
    ProviderPerformance[]
  >([]);
  const [servicePopularity, setServicePopularity] = useState<
    ServicePopularity[]
  >([]);
  const [recentTransactions, setRecentTransactions] = useState<
    RevenueTransaction[]
  >([]);
  const [doctorEarnings, setDoctorEarnings] = useState<DoctorEarnings[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");
  const [userRole, setUserRole] = useState<string>("");
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [dateRange, setDateRange] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end_date: new Date().toISOString().split("T")[0],
  });

  const colorTheme: ColorTheme = {
    primary: "#3b82f6",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    charity: "#ef4444",
    gray50: "#f9fafb",
    gray100: "#f3f4f6",
    gray200: "#e5e7eb",
    gray500: "#6b7280",
    gray600: "#4b5563",
    gray700: "#374151",
    gray900: "#1f2937",
    cardBg: "#ffffff",
  };
  useEffect(() => {
    fetchCurrentUser();
  }, []);
  useEffect(() => {
    // Only load data if user role is set and not secretary
    if (userRole && userRole !== "secretary") {
      loadDashboardData();
    }
  }, [dateRange, userRole]); // ✅ Add userRole as dependency
  // useEffect(() => {
  //   // Get user role from your auth context or API
  //   const fetchUserRole = async () => {
  //     try {
  //       const response = await fetch("/api/user", {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
  //         },
  //       });
  //       const userData = await response.json();
  //       setUserRole(userData.role);
  //     } catch (error) {
  //       console.error("Failed to fetch user role:", error);
  //     }
  //   };

  //   fetchUserRole();
  // }, []);
  const fetchCurrentUser = async (): Promise<void> => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setUserRole("");
        return;
      }

      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL ||
          "https://dr-bassam-elayoubi.caduceuslb.com/api"
        }/user`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);

        // ✅ Handle roles array - get the primary role or check for specific roles
        const roles = userData.roles || [];
        if (roles.includes("admin")) {
          setUserRole("admin");
        } else if (roles.includes("doctor")) {
          setUserRole("doctor");
        } else if (roles.includes("secretary")) {
          setUserRole("secretary");
        } else {
          setUserRole(roles[0] || ""); // Use first role as fallback
        }
      } else {
        setUserRole("");
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      setUserRole("");
    }
  };
  const getAvailableTabs = () => {
    const allTabs = [
      { id: "overview", label: "📊 Overview" },
      { id: "doctors", label: "👨‍⚕️ Doctor Earnings" },
      { id: "analytics", label: "📈 Analytics" }, // Only admin can see this
    ];

    if (userRole === "secretary") {
      return []; // Secretaries see no revenue tabs
    }

    if (userRole === "doctor") {
      return [
        { id: "overview", label: "📊 My Revenue" },
        { id: "doctors", label: "👨‍⚕️ My Earnings" },
        // Analytics tab removed for doctors
      ];
    }

    return allTabs; // Admin sees all
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const loadDashboardData = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Don't load data if user doesn't have access
      if (userRole.includes("secretary") && !userRole.includes("admin")) {
        setLoading(false);
        return;
      }

      const [
        kpisData,
        dailyAnalytics,
        providerData,
        serviceData,
        transactionsData,
        doctorEarningsData,
        referrerEarningsData,
        ownerEarningsData, // This is the problematic one
      ] = await Promise.all([
        RevenueService.getRevenueKPIs(dateRange),
        RevenueService.getDailyAnalytics(30),
        RevenueService.getProviderPerformance(dateRange),
        RevenueService.getServicePopularity(dateRange),
        RevenueService.getTransactions({ ...dateRange, per_page: 10 }),
        RevenueService.getDoctorEarnings(dateRange),
        RevenueService.getReferrerEarnings(dateRange),
        RevenueService.getOwnerEarnings(dateRange),
      ]);

      setKpis(kpisData);
      setDailyData(dailyAnalytics);
      setProviderPerformance(providerData);
      setServicePopularity(serviceData);
      setRecentTransactions(transactionsData.data || []);
      setDoctorEarnings(doctorEarningsData);

      // Fix referrer earnings (in case it has the same issue)
      setReferrerEarnings(
        Array.isArray(referrerEarningsData)
          ? referrerEarningsData
          : Object.values(referrerEarningsData || {})
      );

      // Fix Sponser earnings - convert object to array
      setOwnerEarnings(
        Array.isArray(ownerEarningsData)
          ? ownerEarningsData
          : Object.values(ownerEarningsData || {})
      );
    } catch (err: any) {
      if (err.message.includes("Access denied")) {
        setError("You do not have permission to view this revenue data.");
      } else {
        setError(err.message || "Failed to load revenue data");
      }
      console.error("Error loading revenue dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  // Alternative: You can also create a helper function for this:
  const convertToArray = (data: any): any[] => {
    if (Array.isArray(data)) {
      return data;
    }
    if (data && typeof data === "object") {
      return Object.values(data);
    }
    return [];
  };
  const handleDateRangeChange = (field: string, value: string): void => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: colorTheme.gray50,
        }}
      >
        <div
          style={{
            background: colorTheme.cardBg,
            borderRadius: "16px",
            padding: "32px",
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: `4px solid ${colorTheme.gray200}`,
              borderTop: `4px solid ${colorTheme.primary}`,
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px auto",
            }}
          />
          <p>Loading Revenue Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: "40px",
          textAlign: "center",
          color: colorTheme.error,
        }}
      >
        <h3>Error Loading Revenue Dashboard</h3>
        <p>{error}</p>
        <button
          className="nn"
          onClick={loadDashboardData}
          style={{
            padding: "12px 24px",
            background: colorTheme.primary,
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            marginTop: "16px",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const containerStyle: React.CSSProperties = {
    minHeight: "100vh",
    background: colorTheme.gray50,
    padding: "24px",
  };

  const headerStyle: React.CSSProperties = {
    background: colorTheme.cardBg,
    borderRadius: "16px",
    padding: "32px",
    marginBottom: "24px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
  };

  const cardStyle: React.CSSProperties = {
    background: colorTheme.cardBg,
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
    border: "1px solid #f1f5f9",
  };

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gap: "24px",
    marginBottom: "24px",
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      {/* Header */}
      <div style={headerStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "28px",
                fontWeight: "700",
                color: colorTheme.gray900,
                margin: "0 0 8px 0",
              }}
            >
              💰 Revenue Dashboard
            </h1>
            <p
              style={{
                color: colorTheme.gray500,
                fontSize: "16px",
                margin: 0,
              }}
            >
              Financial analytics and doctor earnings insights
            </p>
          </div>

          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            {/* Date range inputs */}
            <label
              htmlFor="start_date"
              style={{
                fontSize: "14px",
                fontWeight: "500",
                color: colorTheme.gray600,
              }}
            >
              Date Range:
            </label>

            <input
              type="date"
              id="start_date"
              name="start_date"
              value={dateRange.start_date}
              onChange={(e) =>
                handleDateRangeChange("start_date", e.target.value)
              }
              style={{
                padding: "8px 12px",
                border: `1px solid ${colorTheme.gray200}`,
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />

            <span style={{ color: colorTheme.gray500 }}>to</span>
            <input
              type="date"
              id="end_date"
              name="end_date"
              value={dateRange.end_date}
              onChange={(e) =>
                handleDateRangeChange("end_date", e.target.value)
              }
              style={{
                padding: "8px 12px",
                border: `1px solid ${colorTheme.gray200}`,
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>

        {/* ✅ ADD TAB NAVIGATION HERE - USE getAvailableTabs() */}
        {userRole && userRole !== "secretary" && (
          <div
            className="nn"
            style={{
              display: "flex",
              gap: "4px",
              marginTop: "16px",
              padding: "4px",
              background: colorTheme.gray100,
              borderRadius: "12px",
            }}
          >
            {getAvailableTabs().map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  padding: "12px 16px",
                  background:
                    activeTab === tab.id ? colorTheme.primary : "transparent",
                  color: activeTab === tab.id ? "white" : colorTheme.gray600,
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Role-based Access Control - keep this as is */}
        {/* Role-based Access Control */}
        {userRole === "secretary" && (
          <div
            style={{
              ...cardStyle,
              textAlign: "center",
              padding: "60px 40px",
              marginBottom: "24px",
            }}
          >
            <div style={{ fontSize: "64px", marginBottom: "24px" }}>🔒</div>
            <h2
              style={{
                color: colorTheme.gray700,
                marginBottom: "16px",
                fontSize: "24px",
                fontWeight: "600",
              }}
            >
              Access Restricted
            </h2>
            <p
              style={{
                color: colorTheme.gray500,
                fontSize: "16px",
                maxWidth: "400px",
                margin: "0 auto",
              }}
            >
              Revenue information is not available for secretary accounts.
              Please contact your administrator if you need access to financial
              data.
            </p>
          </div>
        )}

        {/* Show loading message while fetching user role */}
        {!userRole && !loading && (
          <div style={{ ...cardStyle, textAlign: "center", padding: "40px" }}>
            <p style={{ color: colorTheme.gray500 }}>
              Loading user permissions...
            </p>
          </div>
        )}
      </div>

      {/* Tab Content */}
      {userRole && userRole !== "secretary" && (
        <>
          {activeTab === "overview" && (
            <>
              {/* KPI Cards - Modified for doctor role */}
              {kpis && (
                <div
                  style={{
                    ...gridStyle,
                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  }}
                >
                  {/* Total Revenue - Only show for admin */}
                  {userRole === "admin" && (
                    <div style={cardStyle}>
                      <h3
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: colorTheme.gray600,
                          margin: "0 0 8px 0",
                          textTransform: "uppercase",
                        }}
                      >
                        Total Revenue
                      </h3>
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: "700",
                          color: colorTheme.gray900,
                          marginBottom: "4px",
                        }}
                      >
                        {RevenueService.formatCurrency(
                          kpis.current_period.total_revenue || 0
                        )}
                      </div>
                      <div
                        style={{
                          color: RevenueService.getGrowthColor(
                            kpis.growth_metrics.revenue_growth_percentage
                          ),
                          fontWeight: "500",
                        }}
                      >
                        {RevenueService.formatPercentage(
                          kpis.growth_metrics.revenue_growth_percentage
                        )}{" "}
                        vs previous period
                      </div>
                    </div>
                  )}

                  {/* Doctor Earnings - Show for all */}
                  <div style={cardStyle}>
                    <h3
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: colorTheme.gray600,
                        margin: "0 0 8px 0",
                        textTransform: "uppercase",
                      }}
                    >
                      {userRole === "doctor"
                        ? "My Earnings"
                        : "Doctor Earnings"}
                    </h3>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: colorTheme.gray900,
                        marginBottom: "4px",
                      }}
                    >
                      {RevenueService.formatCurrency(
                        kpis.current_period.provider_revenue || 0
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: colorTheme.gray500,
                      }}
                    >
                      {userRole === "doctor"
                        ? "Your total earnings"
                        : "Total earned by doctors"}
                    </div>
                  </div>

                  {/* Total Transactions - Show for all */}
                  <div style={cardStyle}>
                    <h3
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: colorTheme.gray600,
                        margin: "0 0 8px 0",
                        textTransform: "uppercase",
                      }}
                    >
                      {userRole === "doctor"
                        ? "My Transactions"
                        : "Total Transactions"}
                    </h3>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: colorTheme.gray900,
                        marginBottom: "4px",
                      }}
                    >
                      {kpis.current_period.total_transactions || 0}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: RevenueService.getGrowthColor(
                          kpis.growth_metrics.transaction_growth_percentage
                        ),
                        fontWeight: "500",
                      }}
                    >
                      {RevenueService.formatPercentage(
                        kpis.growth_metrics.transaction_growth_percentage
                      )}{" "}
                      vs previous period
                    </div>
                  </div>

                  {/* Popular Service - Show for all */}
                  <div style={cardStyle}>
                    <h3
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: colorTheme.gray600,
                        margin: "0 0 8px 0",
                        textTransform: "uppercase",
                      }}
                    >
                      {userRole === "doctor"
                        ? "My Top Service"
                        : "Popular Service"}
                    </h3>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: colorTheme.gray900,
                        marginBottom: "4px",
                      }}
                    >
                      {kpis.popular_service?.name || "No data"}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: colorTheme.gray500,
                      }}
                    >
                      {kpis.popular_service?.usage_count || 0} times used
                    </div>
                  </div>
                </div>
              )}

              {/* Revenue Breakdown - Only show for admin */}
              {kpis && userRole === "admin" && (
                <div
                  style={{
                    ...gridStyle,
                    gridTemplateColumns: "1fr 1fr",
                  }}
                >
                  <div style={cardStyle}>
                    <h3
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        color: colorTheme.gray900,
                        marginBottom: "16px",
                      }}
                    >
                      Revenue Split
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      <div
                        className="nn"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ color: colorTheme.gray600 }}>
                          🏥 Clinic Revenue:
                        </span>
                        <span
                          style={{
                            fontWeight: "600",
                            color: colorTheme.primary,
                          }}
                        >
                          {RevenueService.formatCurrency(
                            kpis.current_period.clinic_revenue || 0
                          )}
                        </span>
                      </div>
                      <div
                        className="nn"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ color: colorTheme.gray600 }}>
                          👨‍⚕️ Worker Revenue:
                        </span>
                        <span
                          style={{
                            fontWeight: "600",
                            color: colorTheme.success,
                          }}
                        >
                          {RevenueService.formatCurrency(
                            kpis.current_period.provider_revenue || 0
                          )}
                        </span>
                      </div>
                      <div
                        className="nn"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ color: colorTheme.gray600 }}>
                          👑 Sponser Revenue:
                        </span>
                        <span
                          style={{
                            fontWeight: "600",
                            color: colorTheme.warning,
                          }}
                        >
                          {RevenueService.formatCurrency(
                            kpis.current_period.owner_revenue || 0
                          )}
                        </span>
                      </div>
                      <div
                        className="nn"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ color: colorTheme.gray600 }}>
                          🤝 Referrer Revenue:
                        </span>
                        <span style={{ fontWeight: "600", color: "#8b5cf6" }}>
                          {RevenueService.formatCurrency(
                            kpis.current_period.referrer_revenue || 0
                          )}
                        </span>
                      </div>
                      <div
                        className="nn"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ color: colorTheme.gray600 }}>
                          ❤️ Charity Revenue:
                        </span>
                        <span
                          style={{
                            fontWeight: "600",
                            color: colorTheme.charity,
                          }}
                        >
                          {RevenueService.formatCurrency(
                            kpis.current_period.charity_revenue || 0
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div style={cardStyle}>
                    <h3
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        color: colorTheme.gray900,
                        marginBottom: "16px",
                      }}
                    >
                      Daily Revenue Trend (Last 30 Days)
                    </h3>
                    <SimpleLineChart
                      data={dailyData}
                      height={200}
                      color={colorTheme.primary}
                    />
                  </div>
                </div>
              )}

              {/* My Earnings Chart - Only show for doctors */}
              {kpis && userRole === "doctor" && (
                <div style={cardStyle}>
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: colorTheme.gray900,
                      marginBottom: "16px",
                    }}
                  >
                    My Daily Earnings (Last 30 Days)
                  </h3>
                  <SimpleLineChart
                    data={dailyData.map((day) => ({
                      ...day,
                      total_revenue: day.provider_revenue || 0, // Show only provider revenue for doctors
                    }))}
                    height={200}
                    color={colorTheme.success}
                  />
                </div>
              )}

              {/* Recent Transactions - Modified for doctor role */}
              <div style={cardStyle}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: colorTheme.gray900,
                      margin: 0,
                    }}
                  >
                    📋{" "}
                    {userRole === "doctor"
                      ? "My Recent Transactions"
                      : "Recent Transactions"}
                  </h3>

                  {/* View Mode Toggle */}
                  <div
                    style={{
                      display: "flex",
                      background: colorTheme.gray100,
                      borderRadius: "8px",
                      padding: "4px",
                    }}
                  >
                    <button
                      onClick={() => setViewMode("cards")}
                      style={{
                        padding: "6px 12px",
                        background:
                          viewMode === "cards"
                            ? colorTheme.primary
                            : "transparent",
                        color:
                          viewMode === "cards" ? "white" : colorTheme.gray600,
                        border: "none",
                        borderRadius: "4px",
                        fontSize: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      📱 Cards
                    </button>
                    <button
                      onClick={() => setViewMode("table")}
                      style={{
                        padding: "6px 12px",
                        background:
                          viewMode === "table"
                            ? colorTheme.primary
                            : "transparent",
                        color:
                          viewMode === "table" ? "white" : colorTheme.gray600,
                        border: "none",
                        borderRadius: "4px",
                        fontSize: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      📊 Table
                    </button>
                  </div>
                </div>

                {recentTransactions.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      color: colorTheme.gray500,
                    }}
                  >
                    <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                      💰
                    </div>
                    <p>
                      {userRole === "doctor"
                        ? "No transactions found for your services in the selected period."
                        : "No transactions found for the selected period."}
                    </p>
                    <p style={{ fontSize: "14px" }}>
                      {userRole === "doctor"
                        ? "Complete some appointments to see your earnings."
                        : "Complete some appointments with services to see revenue data."}
                    </p>
                  </div>
                ) : viewMode === "cards" ? (
                  // Card View - Modified for doctors
                  <div>
                    {recentTransactions.map((transaction: RevenueTransaction) =>
                      // Use modified TransactionCard component for doctors
                      userRole === "doctor" ? (
                        <DoctorTransactionCard
                          key={transaction.id}
                          transaction={transaction}
                          colorTheme={colorTheme}
                        />
                      ) : (
                        <TransactionCard
                          key={transaction.id}
                          transaction={transaction}
                          colorTheme={colorTheme}
                        />
                      )
                    )}
                  </div>
                ) : (
                  // Table View - Modified columns for doctors
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "14px",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            borderBottom: `2px solid ${colorTheme.gray200}`,
                          }}
                        >
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              color: colorTheme.gray600,
                              minWidth: "80px",
                            }}
                          >
                            Date
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              color: colorTheme.gray600,
                              minWidth: "120px",
                            }}
                          >
                            Patient
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              color: colorTheme.gray600,
                              minWidth: "100px",
                            }}
                          >
                            Service
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              color: colorTheme.gray600,
                              minWidth: "100px",
                            }}
                          >
                            Worker
                          </th>
                          {/* Total column - Only show for admin */}
                          {userRole === "admin" && (
                            <th
                              style={{
                                padding: "12px",
                                textAlign: "right",
                                color: colorTheme.gray600,
                                minWidth: "80px",
                              }}
                            >
                              Total
                            </th>
                          )}
                          {/* Provider column - Always show */}
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              color: colorTheme.gray600,
                              minWidth: "70px",
                            }}
                          >
                            👨‍⚕️{" "}
                            {userRole === "doctor" ? "My Earnings" : "Provider"}
                          </th>
                          {/* Other columns - Only show for admin */}
                          {userRole === "admin" && (
                            <>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "right",
                                  color: colorTheme.gray600,
                                  minWidth: "70px",
                                }}
                              >
                                🏥 Clinic
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "right",
                                  color: colorTheme.gray600,
                                  minWidth: "70px",
                                }}
                              >
                                👑 Owner
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "right",
                                  color: colorTheme.gray600,
                                  minWidth: "70px",
                                }}
                              >
                                🤝 Referrer
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "right",
                                  color: colorTheme.gray600,
                                  minWidth: "70px",
                                }}
                              >
                                ❤️ Charity
                              </th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {recentTransactions.map(
                          (transaction: RevenueTransaction, index: number) => (
                            <tr
                              key={transaction.id}
                              style={{
                                borderBottom: `1px solid ${colorTheme.gray100}`,
                                backgroundColor:
                                  index % 2 === 0
                                    ? colorTheme.gray50
                                    : colorTheme.cardBg,
                              }}
                            >
                              <td style={{ padding: "12px", fontSize: "12px" }}>
                                {new Date(
                                  transaction.transaction_date
                                ).toLocaleDateString()}
                              </td>
                              <td
                                style={{ padding: "12px", fontWeight: "500" }}
                              >
                                {transaction.appointment?.patient?.first_name}{" "}
                                {transaction.appointment?.patient?.last_name}
                              </td>
                              <td style={{ padding: "12px" }}>
                                {transaction.service?.name || "Unknown Service"}
                              </td>
                              <td style={{ padding: "12px" }}>
                                {transaction.service?.provider?.first_name}{" "}
                                {transaction.service?.provider?.last_name}
                              </td>
                              {/* Total column - Only for admin */}
                              {userRole === "admin" && (
                                <td
                                  style={{
                                    padding: "12px",
                                    textAlign: "right",
                                    fontWeight: "600",
                                  }}
                                >
                                  {RevenueService.formatCurrency(
                                    transaction.total_amount
                                  )}
                                </td>
                              )}
                              {/* Provider earnings - Always show */}
                              <td
                                style={{
                                  padding: "12px",
                                  textAlign: "right",
                                  color: colorTheme.success,
                                  fontSize: "12px",
                                  fontWeight: "600",
                                }}
                              >
                                {RevenueService.formatCurrency(
                                  transaction.provider_amount
                                )}
                              </td>
                              {/* Other columns - Only for admin */}
                              {userRole === "admin" && (
                                <>
                                  <td
                                    style={{
                                      padding: "12px",
                                      textAlign: "right",
                                      color: colorTheme.primary,
                                      fontSize: "12px",
                                    }}
                                  >
                                    {RevenueService.formatCurrency(
                                      transaction.clinic_amount
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      padding: "12px",
                                      textAlign: "right",
                                      color: colorTheme.warning,
                                      fontSize: "12px",
                                    }}
                                  >
                                    {RevenueService.formatCurrency(
                                      transaction.owner_amount || 0
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      padding: "12px",
                                      textAlign: "right",
                                      color: "#8b5cf6",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {RevenueService.formatCurrency(
                                      transaction.referrer_amount || 0
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      padding: "12px",
                                      textAlign: "right",
                                      color: colorTheme.charity,
                                      fontSize: "12px",
                                    }}
                                  >
                                    {RevenueService.formatCurrency(
                                      transaction.charity_amount || 0
                                    )}
                                  </td>
                                </>
                              )}
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === "doctors" && (
            <div>
              {/* Doctor Earnings Summary - Only show summary cards for admin */}
              {userRole === "admin" && (
                <div
                  style={{
                    ...gridStyle,
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  }}
                >
                  <div style={cardStyle}>
                    <h3
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: colorTheme.gray600,
                        margin: "0 0 8px 0",
                        textTransform: "uppercase",
                      }}
                    >
                      Total Doctor Earnings
                    </h3>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: colorTheme.success,
                      }}
                    >
                      {RevenueService.formatCurrency(
                        Array.isArray(doctorEarnings)
                          ? doctorEarnings.reduce(
                              (sum: number, doc: DoctorEarnings) =>
                                sum + doc.total_earned,
                              0
                            )
                          : 0
                      )}
                    </div>
                  </div>

                  <div style={cardStyle}>
                    <h3
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: colorTheme.gray600,
                        margin: "0 0 8px 0",
                        textTransform: "uppercase",
                      }}
                    >
                      Active Doctors
                    </h3>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: colorTheme.primary,
                      }}
                    >
                      {doctorEarnings.length}
                    </div>
                  </div>

                  <div style={cardStyle}>
                    <h3
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: colorTheme.gray600,
                        margin: "0 0 8px 0",
                        textTransform: "uppercase",
                      }}
                    >
                      Pending Payments
                    </h3>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: colorTheme.warning,
                      }}
                    >
                      {RevenueService.formatCurrency(
                        Array.isArray(doctorEarnings) &&
                          doctorEarnings.length > 0
                          ? doctorEarnings.reduce(
                              (sum: number, doc: DoctorEarnings) =>
                                sum + doc.pending_amount,
                              0
                            )
                          : 0
                      )}
                    </div>
                  </div>

                  <div style={cardStyle}>
                    <h3
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: colorTheme.gray600,
                        margin: "0 0 8px 0",
                        textTransform: "uppercase",
                      }}
                    >
                      Avg per Doctor
                    </h3>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: colorTheme.gray900,
                      }}
                    >
                      {Array.isArray(doctorEarnings) &&
                      doctorEarnings.length > 0
                        ? RevenueService.formatCurrency(
                            doctorEarnings.reduce(
                              (sum: number, doc: DoctorEarnings) =>
                                sum + doc.total_earned,
                              0
                            ) / doctorEarnings.length
                          )
                        : "$0"}
                    </div>
                  </div>
                </div>
              )}

              {/* Doctor Earnings Cards */}
              <div style={cardStyle}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "24px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "20px",
                      fontWeight: "600",
                      color: colorTheme.gray900,
                      margin: 0,
                    }}
                  >
                    👨‍⚕️{" "}
                    {userRole === "doctor"
                      ? "My Earnings Details"
                      : "Doctor Earnings Breakdown"}
                  </h3>
                  {userRole === "admin" && (
                    <button
                      style={{
                        padding: "10px 16px",
                        background: colorTheme.success,
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: "500",
                        cursor: "pointer",
                      }}
                    >
                      💰 Process All Payments
                    </button>
                  )}
                </div>

                {Array.isArray(doctorEarnings) && doctorEarnings.length > 0 ? (
                  doctorEarnings.map((doctor: DoctorEarnings) => (
                    <DoctorEarningsCard
                      key={doctor.doctor_id}
                      doctor={doctor}
                      colorTheme={colorTheme}
                    />
                  ))
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      color: colorTheme.gray500,
                    }}
                  >
                    <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                      👨‍⚕️
                    </div>
                    <p>
                      {userRole === "doctor"
                        ? "No earnings found for the selected period."
                        : "No doctor earnings found for the selected period."}
                    </p>
                  </div>
                )}
              </div>

              {/* Referrer and Sponser Earnings - Only show for admin */}
              {userRole === "admin" && (
                <>
                  <div style={cardStyle}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "24px",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "20px",
                          fontWeight: "600",
                          color: colorTheme.gray900,
                          margin: 0,
                        }}
                      >
                        🤝 Referrer Earnings Breakdown
                      </h3>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <div
                          style={{
                            padding: "8px 12px",
                            background: "#8b5cf6",
                            color: "white",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "500",
                          }}
                        >
                          Total:{" "}
                          {RevenueService.formatCurrency(
                            (referrerEarnings || []).reduce(
                              (sum, ref) => sum + ref.total_earned,
                              0
                            )
                          )}
                        </div>
                        <button
                          style={{
                            padding: "10px 16px",
                            background: "#8b5cf6",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: "500",
                            cursor: "pointer",
                          }}
                        >
                          💰 Process All Payments
                        </button>
                      </div>
                    </div>

                    {Array.isArray(referrerEarnings) &&
                    referrerEarnings.length > 0 ? (
                      referrerEarnings.map((referrer: ReferrerEarnings) => (
                        <ReferrerEarningsCard
                          key={`${referrer.referrer_id}-${referrer.referrer_name}`}
                          referrer={referrer}
                          colorTheme={colorTheme}
                        />
                      ))
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: colorTheme.gray500,
                        }}
                      >
                        <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                          🤝
                        </div>
                        <p>
                          No referrer earnings found for the selected period.
                        </p>
                        <p style={{ fontSize: "14px" }}>
                          Referrer earnings will appear when appointments with
                          referrer information are completed.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Sponser Earnings Section - Add null check */}
                  <div style={cardStyle}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "24px",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "20px",
                          fontWeight: "600",
                          color: colorTheme.gray900,
                          margin: 0,
                        }}
                      >
                        👑 Equipment Sponser Earnings
                      </h3>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <div
                          style={{
                            padding: "8px 12px",
                            background: colorTheme.warning,
                            color: "white",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "500",
                          }}
                        >
                          Total:{" "}
                          {RevenueService.formatCurrency(
                            Array.isArray(ownerEarnings) &&
                              ownerEarnings.length > 0
                              ? ownerEarnings.reduce(
                                  (sum, owner) => sum + owner.total_earned,
                                  0
                                )
                              : 0
                          )}
                        </div>
                        <button
                          style={{
                            padding: "10px 16px",
                            background: colorTheme.warning,
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: "500",
                            cursor: "pointer",
                          }}
                        >
                          💰 Process All Payments
                        </button>
                      </div>
                    </div>

                    {Array.isArray(ownerEarnings) &&
                    ownerEarnings.length > 0 ? (
                      ownerEarnings.map((owner: OwnerEarnings) => (
                        <OwnerEarningsCard
                          key={owner.owner_id}
                          owner={owner}
                          colorTheme={colorTheme}
                        />
                      ))
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: colorTheme.gray500,
                        }}
                      >
                        <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                          👑
                        </div>
                        <p>
                          No equipment sponser earnings found for the selected
                          period.
                        </p>
                        <p style={{ fontSize: "14px" }}>
                          Sponser earnings will appear when services using their
                          equipment are completed.
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "analytics" && userRole === "admin" && (
            <div style={{ ...gridStyle, gridTemplateColumns: "1fr 1fr" }}>
              <div style={cardStyle}>
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: colorTheme.gray900,
                    marginBottom: "16px",
                  }}
                >
                  👨‍⚕️ Worker Performance
                </h3>
                <SimpleBarChart
                  data={providerPerformance}
                  labelKey="provider_name"
                  valueKey="total_revenue"
                  height={300}
                  color={colorTheme.success}
                />
              </div>

              <div style={cardStyle}>
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: colorTheme.gray900,
                    marginBottom: "16px",
                  }}
                >
                  ⚕️ Service Popularity
                </h3>
                <SimpleBarChart
                  data={servicePopularity}
                  labelKey="service_name"
                  valueKey="usage_count"
                  height={300}
                  color={colorTheme.warning}
                />
              </div>

              {/* Revenue Split Breakdown */}
              {kpis && (
                <div style={cardStyle}>
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: colorTheme.gray900,
                      marginBottom: "16px",
                    }}
                  >
                    Revenue Distribution
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ color: colorTheme.gray600 }}>
                        🏥 Clinic Revenue:
                      </span>
                      <span
                        style={{ fontWeight: "600", color: colorTheme.primary }}
                      >
                        {RevenueService.formatCurrency(
                          kpis.current_period.clinic_revenue || 0
                        )}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ color: colorTheme.gray600 }}>
                        👨‍⚕️ Worker Revenue:
                      </span>
                      <span
                        style={{ fontWeight: "600", color: colorTheme.success }}
                      >
                        {RevenueService.formatCurrency(
                          kpis.current_period.provider_revenue || 0
                        )}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ color: colorTheme.gray600 }}>
                        👑 Sponser Revenue:
                      </span>
                      <span
                        style={{ fontWeight: "600", color: colorTheme.warning }}
                      >
                        {RevenueService.formatCurrency(
                          kpis.current_period.owner_revenue || 0
                        )}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ color: colorTheme.gray600 }}>
                        🤝 Referrer Revenue:
                      </span>
                      <span style={{ fontWeight: "600", color: "#8b5cf6" }}>
                        {RevenueService.formatCurrency(
                          kpis.current_period.referrer_revenue || 0
                        )}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ color: colorTheme.gray600 }}>
                        ❤️ Charity Revenue:
                      </span>
                      <span
                        style={{ fontWeight: "600", color: colorTheme.charity }}
                      >
                        {RevenueService.formatCurrency(
                          kpis.current_period.charity_revenue || 0
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Daily Revenue Trend */}
              <div style={cardStyle}>
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: colorTheme.gray900,
                    marginBottom: "16px",
                  }}
                >
                  Daily Revenue Trend (Last 30 Days)
                </h3>
                <SimpleLineChart
                  data={dailyData}
                  height={200}
                  color={colorTheme.primary}
                />
              </div>
            </div>
          )}
        </>
      )}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default RevenueDashboard;
